import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useToast } from '../../contexts/ToastContext';
import { supabase } from '../../lib/supabaseClient';
import { generateQRCode } from '../../lib/qrCodeGenerator';
import UpgradeModal from '../../components/UpgradeModal';
import { getNextPlan, PLAN_LIMITS } from '../../lib/planLimits';
import { 
  Plus, 
  Edit, 
  Trash, 
  Copy, 
  QrCode, 
  Download,
  ToggleRight,
  ToggleLeft,
  Search,
  Eye,
  ExternalLink,
  AlertTriangle,
  Mail
} from 'lucide-react';

const FormsPage = () => {
  const { user } = useAuth();
  const { planLimits, currentPlan, isTrialExpired, isSubscribed } = useSubscription();
  const { addToast } = useToast();
  const [forms, setForms] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedForm, setSelectedForm] = useState<any>(null);
  const [showQrModal, setShowQrModal] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [loadingQr, setLoadingQr] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  useEffect(() => {
    if (user) {
      fetchForms();
    }
  }, [user]);

  const fetchForms = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('forms')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setForms(data || []);
    } catch (error) {
      addToast('Failed to fetch forms', 'error');
    }
  };

  const checkFormLimit = () => {
    if (planLimits.maxForms === -1) return true; // Unlimited
    return forms.length < planLimits.maxForms;
  };

  const handleCreateForm = () => {
    if (!checkFormLimit()) {
      setShowUpgradeModal(true);
      return;
    }
    // Navigate to form creation (this will be handled by the Link component)
  };

  const handleToggleActive = async (id: string, currentStatus: boolean) => {
    // Block any form changes for expired trial users without subscription
    if (isTrialExpired && !isSubscribed) {
      addToast('Cannot modify forms - subscription required. Please upgrade your plan.', 'error');
      return;
    }

    try {
      const { error } = await supabase
        .from('forms')
        .update({ is_active: !currentStatus })
        .eq('id', id);

      if (error) throw error;
      
      setForms(forms.map(form => 
        form.id === id ? { ...form, is_active: !currentStatus } : form
      ));
      
      addToast(`Form ${!currentStatus ? 'activated' : 'deactivated'} successfully`, 'success');
    } catch (error) {
      addToast('Failed to update form status', 'error');
    }
  };

  const handleToggleEmailNotifications = async (id: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('forms')
        .update({ email_notifications_enabled: !currentStatus })
        .eq('id', id);

      if (error) throw error;
      
      setForms(forms.map(form => 
        form.id === id ? { ...form, email_notifications_enabled: !currentStatus } : form
      ));
      
      addToast(`Email notifications ${!currentStatus ? 'enabled' : 'disabled'} successfully`, 'success');
    } catch (error) {
      addToast('Failed to update email notification settings', 'error');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      setDeletingId(id);
      const { error } = await supabase
        .from('forms')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setForms(forms.filter(form => form.id !== id));
      addToast('Form deleted successfully', 'success');
      setShowDeleteModal(false);
    } catch (error) {
      addToast('Failed to delete form', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const confirmDelete = (form: any) => {
    setSelectedForm(form);
    setShowDeleteModal(true);
  };

  const copyFormLink = (form: any) => {
    const url = `${window.location.origin}/feedback/${form.id}`;
    navigator.clipboard.writeText(url);
    addToast('Form link copied to clipboard', 'success');
  };

  const showQrCode = async (form: any) => {
    // Block QR code generation for expired trial users
    if (isTrialExpired && !isSubscribed) {
      addToast('QR code unavailable - subscription required. Please upgrade your plan.', 'error');
      return;
    }

    try {
      setSelectedForm(form);
      setLoadingQr(true);
      setShowQrModal(true);
      
      const url = `${window.location.origin}/feedback/${form.id}`;
      const qrCode = await generateQRCode(url);
      setQrCodeDataUrl(qrCode);
    } catch (error) {
      addToast('Failed to generate QR code', 'error');
      setShowQrModal(false);
    } finally {
      setLoadingQr(false);
    }
  };

  const downloadQrCode = () => {
    const link = document.createElement('a');
    const formName = selectedForm.name.replace(/\s+/g, '-').toLowerCase();
    link.download = `qr-feedback-${formName}.png`;
    link.href = qrCodeDataUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredForms = forms.filter(form => 
    form.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Feedback Forms</h1>
          <p className="text-sm text-gray-500">
            Manage your feedback forms and QR codes
          </p>
        </div>

        <div className="mt-4 md:mt-0">
          {checkFormLimit() ? (
            <Link
              to="/dashboard/forms/new"
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Plus size={16} className="mr-2" />
              Create New Form
            </Link>
          ) : (
            <button
              onClick={handleCreateForm}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Plus size={16} className="mr-2" />
              Create New Form
            </button>
          )}
        </div>
      </div>

      {/* Trial Expired Warning */}
      {isTrialExpired && !isSubscribed && (
        <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Forms Deactivated - Trial Expired
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p className="mb-2">
                  Your trial has expired and all forms have been automatically deactivated. 
                  QR codes and form URLs are not accessible to the public.
                </p>
                <p>
                  <Link 
                    to="/dashboard/billing" 
                    className="font-medium text-red-800 underline hover:text-red-900"
                  >
                    Subscribe to a plan
                  </Link> to reactivate all forms immediately.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Plan Limits Info */}
      {planLimits.maxForms !== -1 && (
        <div className="mb-4 bg-blue-50 border-l-4 border-blue-400 p-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                <strong>Forms:</strong> {forms.length} of {planLimits.maxForms} used
                {forms.length >= planLimits.maxForms && (
                  <span className="ml-2 text-red-600 font-medium">
                    (Limit reached - upgrade to create more forms)
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="flex mb-6">
        <div className="relative flex-grow">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
            placeholder="Search forms..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Forms list */}
      {filteredForms.length > 0 ? (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {filteredForms.map((form) => (
              <li key={form.id}>
                <div className="px-4 py-4 flex items-center sm:px-6">
                  <div className="min-w-0 flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <div className="flex text-sm">
                        <p className="font-medium text-blue-600 truncate">{form.name}</p>
                        <p className="ml-1 flex-shrink-0 font-normal text-gray-500">
                          {form.is_active ? (
                            <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                              Active
                            </span>
                          ) : (
                            <span className={`ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              isTrialExpired && !isSubscribed 
                                ? 'bg-red-100 text-red-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {isTrialExpired && !isSubscribed ? 'Subscription Required' : 'Inactive'}
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="mt-2 flex">
                        <div className="flex items-center text-sm text-gray-500">
                          <p>
                            Created on {new Date(form.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-4 flex flex-wrap gap-2 sm:mt-0 sm:flex-nowrap">
                      <button
                        onClick={() => handleToggleActive(form.id, form.is_active)}
                        className={`${
                          isTrialExpired && !isSubscribed
                            ? 'text-gray-300 cursor-not-allowed' 
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                        title={
                          isTrialExpired && !isSubscribed
                            ? 'Cannot modify forms - subscription required'
                            : form.is_active 
                            ? 'Deactivate form' 
                            : 'Activate form'
                        }
                        disabled={isTrialExpired && !isSubscribed}
                      >
                        {form.is_active ? (
                          <ToggleRight size={20} className={
                            isTrialExpired && !isSubscribed ? 'text-gray-300' : 'text-green-500'
                          } />
                        ) : (
                          <ToggleLeft size={20} className={
                            isTrialExpired && !isSubscribed ? 'text-gray-300' : ''
                          } />
                        )}
                      </button>
                      
                      <button
                        onClick={() => handleToggleEmailNotifications(form.id, form.email_notifications_enabled)}
                        className="text-gray-500 hover:text-gray-700"
                        title={form.email_notifications_enabled ? 'Disable email notifications' : 'Enable email notifications'}
                      >
                        <Mail size={20} className={form.email_notifications_enabled ? 'text-blue-500' : ''} />
                      </button>
                      
                      {isTrialExpired && !isSubscribed ? (
                        <button
                          className="text-gray-300 cursor-not-allowed"
                          title="Preview unavailable - subscription required"
                          disabled
                        >
                          <Eye size={20} />
                        </button>
                      ) : (
                        <Link
                          to={`/feedback/${form.id}`}
                          target="_blank"
                          className="text-gray-500 hover:text-gray-700"
                          title="Preview form"
                        >
                          <Eye size={20} />
                        </Link>
                      )}
                      
                      {isTrialExpired && !isSubscribed ? (
                        <button
                          className="text-gray-300 cursor-not-allowed"
                          title="Link copying unavailable - subscription required"
                          disabled
                        >
                          <Copy size={20} />
                        </button>
                      ) : (
                        <button
                          onClick={() => copyFormLink(form)}
                          className="text-gray-500 hover:text-gray-700"
                          title="Copy form link"
                        >
                          <Copy size={20} />
                        </button>
                      )}
                      
                      <button
                        onClick={() => showQrCode(form)}
                        className={
                          isTrialExpired && !isSubscribed
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-gray-500 hover:text-gray-700'
                        }
                        title={
                          isTrialExpired && !isSubscribed
                            ? 'QR code unavailable - subscription required'
                            : 'View QR code'
                        }
                        disabled={isTrialExpired && !isSubscribed}
                      >
                        <QrCode size={20} />
                      </button>
                      
                      {isTrialExpired && !isSubscribed ? (
                        <button
                          className="text-gray-300 cursor-not-allowed"
                          title="Edit unavailable - subscription required"
                          disabled
                        >
                          <Edit size={20} />
                        </button>
                      ) : (
                        <Link
                          to={`/dashboard/forms/${form.id}`}
                          className="text-gray-500 hover:text-gray-700"
                          title="Edit form"
                        >
                          <Edit size={20} />
                        </Link>
                      )}
                      
                      <button
                        onClick={() => confirmDelete(form)}
                        className="text-gray-500 hover:text-red-500"
                        title="Delete form"
                      >
                        <Trash size={20} />
                      </button>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="text-center py-12 bg-white shadow rounded-lg">
          <QrCode size={48} className="mx-auto text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No forms found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm ? 'No forms match your search criteria.' : 'Get started by creating a new form.'}
          </p>
          {!searchTerm && (
            <div className="mt-6">
              {checkFormLimit() ? (
                <Link
                  to="/dashboard/forms/new"
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  <Plus size={16} className="mr-2" />
                  Create New Form
                </Link>
              ) : (
                <button
                  onClick={handleCreateForm}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  <Plus size={16} className="mr-2" />
                  Create New Form
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* QR Code Modal */}
      {showQrModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <div className="mt-3 text-center sm:mt-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    QR Code for {selectedForm?.name}
                  </h3>
                  
                  {isTrialExpired && !isSubscribed ? (
                    <div className="mt-4 flex flex-col items-center">
                      <div className="bg-red-50 border-l-4 border-red-400 p-4 w-full">
                        <div className="flex">
                          <div className="flex-shrink-0">
                            <AlertTriangle className="h-5 w-5 text-red-400" />
                          </div>
                          <div className="ml-3">
                            <h3 className="text-sm font-medium text-red-800">
                              QR Code Currently Inactive
                            </h3>
                            <div className="mt-2 text-sm text-red-700">
                              <p className="mb-2">
                                This QR code will show a "subscription required" message when scanned. 
                                Visitors cannot access the form until you subscribe to a plan.
                              </p>
                              <p>
                                <Link 
                                  to="/dashboard/billing" 
                                  className="font-medium text-red-800 underline hover:text-red-900"
                                  onClick={() => setShowQrModal(false)}
                                >
                                  Subscribe now to activate
                                </Link>
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="mt-4 bg-white p-4 rounded-lg shadow-sm border border-gray-200 opacity-50">
                        <img 
                          src={qrCodeDataUrl} 
                          alt="QR Code (Inactive)" 
                          className="w-64 h-64"
                        />
                      </div>
                      <p className="mt-2 text-sm text-gray-400">
                        QR code is inactive - subscription required
                      </p>
                    </div>
                  ) : (
                    <div className="mt-4 flex flex-col items-center">
                      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                        <img 
                          src={qrCodeDataUrl} 
                          alt="QR Code" 
                          className="w-64 h-64"
                        />
                      </div>
                      <p className="mt-2 text-sm text-gray-500">
                        Scan this QR code to access the feedback form
                      </p>
                      <div className="mt-4 flex flex-col sm:flex-row gap-3">
                        <button
                          type="button"
                          onClick={downloadQrCode}
                          className="inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          <Download size={16} className="mr-2" />
                          Download QR Code
                        </button>
                        <a
                          href={`/feedback/${selectedForm.id}`}
                          target="_blank"
                          className="inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          <ExternalLink size={16} className="mr-2" />
                          Open Form
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-5 sm:mt-6">
                <button
                  type="button"
                  onClick={() => setShowQrModal(false)}
                  className="inline-flex justify-center w-full rounded-md border border-transparent shadow-sm px-4 py-2 bg-gray-600 text-base font-medium text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <div className="mt-3 text-center sm:mt-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Delete Form
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Are you sure you want to delete the form "{selectedForm?.name}"? This action cannot be undone.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                  type="button"
                  onClick={() => handleDelete(selectedForm.id)}
                  disabled={deletingId !== null}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:col-start-2 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Delete
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature="Forms"
        currentLimit={planLimits.maxForms}
        nextPlanLimit={getNextPlan(currentPlan) ? PLAN_LIMITS[getNextPlan(currentPlan)!].maxForms : -1}
        currentPlan={currentPlan}
      />
    </div>
  );
};

export default FormsPage;